import { callAction } from './client'
import { useAction, useActionQuery } from './hooks'

/** Web push (PWA) channel of the staff app (internal/comms/webpush*.go). */
export const WebPushA = {
  config: 'webpush.config',
  subscribe: 'webpush.subscribe',
  unsubscribe: 'webpush.unsubscribe',
  devices: 'webpush.devices',
  test: 'webpush.test',
  prefsSet: 'webpush.prefs_set',
  health: 'webpush.health',
  installed: 'pwa.installed',
} as const

export interface WebPushPrefs {
  enabled: boolean
  muted_kinds: string[] | null
}

export interface WebPushConfig {
  enabled: boolean
  public_key: string
  key_id: string
  prefs: WebPushPrefs
  devices: number
}

export interface WebPushDevice {
  id: string
  app: string
  device_label: string
  user_agent: string
  endpoint_host: string
  endpoint_hash: string
  standalone: boolean
  created_at: string
  last_success_at?: string | null
  failure_count: number
  last_error?: string
}

export interface WebPushTestResult {
  devices: number
  sent: number
  failed: number
  revoked: number
}

export interface WebPushFailure {
  at: string
  subject_type: string
  device_label: string
  endpoint_host: string
  failure_count: number
  error: string
  revoked_reason?: string
}

export interface WebPushHealth {
  configured: boolean
  key_source: string
  key_id: string
  staff_active: number
  staff_subscribed: number
  staff_devices: number
  staff_installed: number
  clients_subscribed: number
  client_devices: number
  clients_installed: number
  sent_7d: number
  failed_7d: number
  success_rate?: number | null
  revoked_7d: number
  recent_failures: WebPushFailure[] | null
}

export const useWebPushConfig = () => useActionQuery<WebPushConfig>(WebPushA.config, {})
export const useWebPushDevices = () => useActionQuery<WebPushDevice[]>(WebPushA.devices, {})
export const useWebPushHealth = () => useActionQuery<WebPushHealth>(WebPushA.health, {}, { refetchInterval: 60_000 })
export const useWebPushUnsubscribe = () => useAction<{ id?: string; endpoint?: string }, { removed: number }>(WebPushA.unsubscribe)
export const useWebPushTest = () => useAction<Record<string, never>, WebPushTestResult>(WebPushA.test)
export const useWebPushPrefs = () => useAction<{ enabled: boolean; muted_kinds?: string[] }, WebPushPrefs>(WebPushA.prefsSet)

export const saveSubscription = (subscription: PushSubscriptionJSON, device_label: string, standalone: boolean) =>
  callAction<WebPushDevice>(WebPushA.subscribe, { subscription, device_label, standalone })

export const removeSubscription = (endpoint: string) => callAction<{ removed: number }>(WebPushA.unsubscribe, { endpoint })

export const reportInstalled = (platform: string) => callAction<{ ok: boolean }>(WebPushA.installed, { platform })

/**
 * Staff notification kinds that can be muted for push, grouped for the settings
 * page. Kinds not listed here are always pushed (when push is on).
 */
export const PUSH_KIND_GROUPS: { group: string; kinds: string[] }[] = [
  { group: 'tasks', kinds: ['task_assigned', 'task_comment', 'task_due'] },
  { group: 'process', kinds: ['checklist_due', 'checklist_overdue', 'checklist_attention'] },
  { group: 'clinic', kinds: ['ward_check_overdue', 'ward_report_due', 'ward_handover', 'ai_draft_ready', 'shipment_overdue', 'shipment_result', 'blood_unit_expiry', 'control_calls_due', 'clinq_digest'] },
  { group: 'alerts', kinds: ['incident_new', 'incident_sla', 'incident_update', 'code_alert', 'low_rating_alert'] },
  { group: 'desk', kinds: ['inquiry_sla', 'inquiry_handover', 'waitlist_slot', 'consent_answered'] },
  { group: 'money', kinds: ['cash_alert', 'night_payment', 'discount_request', 'fop_limit', 'loan_due', 'fincontrol_finding', 'invoice_payment_due', 'invoice_discrepancy'] },
  { group: 'people', kinds: ['kb_ack_request', 'kb_review_due', 'briefing_due', 'certificate_expiry', 'register_expiry', 'content_deadline', 'content_review'] },
  { group: 'stock', kinds: ['stock_expiry', 'stock_to_order', 'stock_count_differences'] },
  { group: 'system', kinds: ['sync_alert', 'insight_published'] },
]
